import { useEffect, useRef } from "react";

/**
 * Adds `is-visible` class to the element when it scrolls into view.
 * `staggerChildrenMs` reveals direct children with a delay between each.
 */
export function useReveal({ threshold = 0.15, staggerChildrenMs = 0, rootMargin = "0px 0px -10% 0px" } = {}) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      el.classList.add("is-visible");
      if (staggerChildrenMs > 0) {
        el.querySelectorAll(".reveal").forEach((kid) => kid.classList.add("is-visible"));
      }
      return;
    }

    // Reveal big sections (taller than viewport) the moment any pixel enters
    // the viewport. The default 0.15 threshold can never be met when the
    // target is taller than 1/0.15 ≈ 6.7x the viewport height — which can
    // happen on portfolio grids with many cards. Falling back to threshold 0
    // for those keeps the reveal animation working on every viewport.
    const safeThreshold = el.getBoundingClientRect().height > window.innerHeight * 1.5
      ? 0
      : threshold;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            if (staggerChildrenMs > 0) {
              const kids = entry.target.querySelectorAll(".reveal");
              kids.forEach((kid, i) => {
                kid.style.transitionDelay = `${i * staggerChildrenMs}ms`;
                kid.classList.add("is-visible");
              });
            }
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: safeThreshold, rootMargin }
    );
    observer.observe(el);

    // Safety net: if for any reason the observer never fires within 1.2s
    // (e.g. element already fully in view but threshold edge-case), force
    // the reveal so the page never sits invisible.
    const safety = setTimeout(() => {
      const rect = el.getBoundingClientRect();
      const inViewport = rect.top < window.innerHeight && rect.bottom > 0;
      if (inViewport && !el.classList.contains("is-visible")) {
        el.classList.add("is-visible");
        if (staggerChildrenMs > 0) {
          el.querySelectorAll(".reveal").forEach((kid, i) => {
            kid.style.transitionDelay = `${i * staggerChildrenMs}ms`;
            kid.classList.add("is-visible");
          });
        }
        observer.unobserve(el);
      }
    }, 1200);

    return () => {
      clearTimeout(safety);
      observer.disconnect();
    };
  }, [threshold, staggerChildrenMs, rootMargin]);

  return ref;
}
